import { Link, useParams, useNavigate, useLocation } from "react-router-dom"
import { useContext, useEffect, useState, useCallback } from "react"
import PopupModal from "../../../components/modals/PopupModal"
import UsersTable from "../../../components/tables/UsersTable"
import LocationWalkReportsTable from "../../../components/tables/LocationWalkReportsTable"
import DataContext from "../../../context/dataProvider"
import { useQuery } from "@tanstack/react-query"
import UiContext from "../../../context/uiProvider"
import { TABLET } from "../../../utils/screenSize"
import { locationService } from "../../../services/locationService"
import { AREA_MANAGER } from "../../../utils/roles"
import { incidentService } from "../../../services/IncidentService"
import IncidentsView from "../../../components/general/IncidentsView"
import { Incident } from "../../../Interfaces"
import CreatWalks from "./createWalks"

export default function SingleLocation() {
  const { deviceView } = useContext(UiContext)
  const { setCurrentLocation, currentUser } = useContext(DataContext)
  const { id } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const locationId = Number(id)
  const [showPopupModal, setShowPopupModal] = useState<boolean>(false)
  const [activeTab, setActiveTab] = useState<string>("Walk Reports")
  const [incidents, setIncidents] = useState<Incident[]>([])
  const [showUnresolvedOnly, setShowUnresolvedOnly] = useState<boolean>(true)

  const { data: currentLocation } = useQuery(["location", locationId], () => locationService.getOne(Number(locationId)), {
    enabled: locationId !== undefined,
  })

  const { data: organizationName } = useQuery<string>(["organization-name", locationId], () => locationService.getOrganizationName(locationId), {
    enabled: locationId !== undefined,
  })

  const { data: managementCompanyName } = useQuery<string>(["management-company-name", locationId], () => locationService.getManagementCompanyName(locationId), {
    enabled: locationId !== undefined,
  })

  const fetchIncidents = useCallback(async () => {
    try {
      const response = await incidentService.getAllByLocation({
        locationId,
        unresolvedOnly: showUnresolvedOnly
      })
      setIncidents(response?.incidents || [])
    } catch (error) {
      console.error("Error fetching incidents:", error)
      setIncidents([])
    }
  }, [locationId, showUnresolvedOnly])

  useEffect(() => {
    if (currentLocation) {
      setCurrentLocation(currentLocation)
    }
  }, [currentLocation, setCurrentLocation])

  useEffect(() => {
    if (locationId) {
      fetchIncidents()
    }
  }, [locationId, showUnresolvedOnly, fetchIncidents])

  // Handle hash-based navigation
  useEffect(() => {
    const hash = location.hash.replace('#', '')
    if (hash) {
      const validTabs = ['walk-reports', 'employees', 'incidents', 'setup-walks', 'update-location']
      if (validTabs.includes(hash)) {
        const tabMap: { [key: string]: string } = {
          'walk-reports': 'Walk Reports',
          'employees': 'Employees',
          'incidents': 'Incidents',
          'setup-walks': 'Setup Walks',
          'update-location': 'Update Location'
        }
        setActiveTab(tabMap[hash])
      }
    } else {
      // Default to walk reports if no hash is present
      setActiveTab('Walk Reports')
      navigate(`/app/locations/${locationId}#walk-reports`, { replace: true })
    }
  }, [location.hash, locationId, navigate])

  const handleTabClick = (tabName: string) => {
    const hashMap: { [key: string]: string } = {
      'Walk Reports': 'walk-reports',
      'Employees': 'employees',
      'Incidents': 'incidents',
      'Setup Walks': 'setup-walks',
      'Update Location': 'update-location'
    }

    setActiveTab(tabName)
    const hash = hashMap[tabName]
    if (hash) {
      navigate(`/app/locations/${locationId}#${hash}`, { replace: true })
    }
  }

  if (!locationId) return null

  return (
    <>
      <div className="w-full min-h-full flex justify-center overflow-auto h-full">
        <div className="w-full flex flex-col">
          <section className="bg-secondary-extra-light p-2 md:rounded-lg mb-2 shadow-lg">
            <div className="bg-primary-dark flex flex-col">
              <div className="text-white rounded-lg p-4">
                <div className={`${deviceView > TABLET && "flex"}`}>
                  <div className="mr-5">
                    <h3 className="">{currentLocation.name}</h3>
                    <h4 className="">{currentLocation.address}</h4>
                    <h4 className="">{`${currentLocation.city}, ${currentLocation.state} ${currentLocation.zipcode}`}</h4>
                  </div>
                  <div>
                    {managementCompanyName && (
                      <h4>
                        Management Company:<span className="text-base">{" " + managementCompanyName}</span>
                      </h4>
                    )}
                    <h4 className="">
                      Area:<span className="text-base">{" " + organizationName}</span>
                    </h4>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Tabs */}
          <div className="bg-white shadow-lg rounded-lg mb-4">
            <div className="border-b border-gray-200">
              <nav className="flex -mb-px">
                <button
                  onClick={() => handleTabClick("Walk Reports")}
                  className={`py-4 px-6 text-center border-b-2 font-medium text-sm ${activeTab === "Walk Reports"
                    ? "border-primary text-primary"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                    }`}
                >
                  Walk Reports
                </button>
                <button
                  onClick={() => handleTabClick("Employees")}
                  className={`py-4 px-6 text-center border-b-2 font-medium text-sm ${activeTab === "Employees"
                    ? "border-primary text-primary"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                    }`}
                >
                  Employees
                </button>
                <button
                  onClick={() => handleTabClick("Incidents")}
                  className={`py-4 px-6 text-center border-b-2 font-medium text-sm ${activeTab === "Incidents"
                    ? "border-primary text-primary"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                    }`}
                >
                  Incidents
                </button>
                <button
                  onClick={() => handleTabClick("Setup Walks")}
                  className={`py-4 px-6 text-center border-b-2 font-medium text-sm ${activeTab === "Setup Walks"
                    ? "border-primary text-primary"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                    }`}
                >
                  Setup Walks
                </button>
                {currentUser?.roleId && currentUser.roleId >= AREA_MANAGER && (
                  <Link
                    to={`../locations/update/${id}`}
                    className={`py-4 px-6 text-center border-b-2 font-medium text-sm ${activeTab === "Update Location"
                      ? "border-primary text-primary"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                      }`}
                    state={{ currentLocation, organizationName }}
                  >
                    Update Location
                  </Link>
                )}
              </nav>
            </div>
          </div>

          {/* Tab Content */}
          <div className="bg-white shadow-lg rounded-lg p-4">
            {activeTab === "Walk Reports" && <LocationWalkReportsTable locationId={locationId} />}
            {activeTab === "Employees" && (
              <UsersTable
                dataSource="location"
                sourceId={locationId}
                title="Location Employees"
              />
            )}
            {activeTab === "Incidents" && (
              <IncidentsView
                locationId={locationId}
                incidents={incidents}
                showUnresolvedOnly={showUnresolvedOnly}
                setShowUnresolvedOnly={setShowUnresolvedOnly}
                coordinates={{ lat: currentLocation.latitude, lng: currentLocation.longitude }}
                zoom={currentLocation.zoomLevel || 18}
              />
            )}
            {activeTab === "Setup Walks" && (
              <CreatWalks />
            )}
          </div>
        </div>
        {showPopupModal && (
          <PopupModal isOpen={showPopupModal} onCancel={() => setShowPopupModal(false)}>
            <div className="w-full flex flex-wrap sm:rounded-lg m-4 p-4">
              <UsersTable
                dataSource="location"
                sourceId={currentLocation.id || 0}
                title="Location Employees"
              />
            </div>
          </PopupModal>
        )}
      </div>
    </>
  )
}